import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRankingsOverview, getFleetOpportunities } from '../apiRankings';
import PageHeader from '../ui/PageHeader';
import Alert from '../ui/Alert';
import Spinner from '../ui/Spinner';
import { formatCount } from '../pageMetrics';
import { sortRows, formatPos, matchesText } from './rankings';
import { Kpi, SortTh, PosDelta, PctDelta, TrendCell } from './parts';
import { ValueTable } from './DomainRankings';

const DAY_OPTIONS = [7, 28, 90];

/**
 * Every site's Google performance in one table (Search Console, all verified
 * properties), and the best "just below the top" opportunities across them.
 */
const RankingsFleet = () => {
  const [days, setDays] = useState(28);
  const [overview, setOverview] = useState(null);
  const [opps, setOpps] = useState(null);
  const [sort, setSort] = useState('clicks');
  const [text, setText] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    let live = true;
    setOverview(null);
    getRankingsOverview(days).then((d) => live && setOverview(d));
    return () => { live = false; };
  }, [days]);
  useEffect(() => { getFleetOpportunities(50).then(setOpps); }, []);

  const sites = useMemo(() => (overview && overview.sites) || [], [overview]);
  const hasData = (s) => (s.impressions || 0) > 0 || (s.prev_impressions || 0) > 0;
  const emptyCount = sites.filter((s) => !hasData(s)).length;
  const rows = useMemo(() => sortRows(sites.filter((s) => (showEmpty || hasData(s)) && matchesText(s, text, 'domain_id')), sort),
    [sites, text, sort, showEmpty]);
  const tot = useMemo(() => sites.reduce((a, s) => ({
    clicks: a.clicks + (s.clicks || 0), impressions: a.impressions + (s.impressions || 0),
    prev_clicks: a.prev_clicks + (s.prev_clicks || 0), prev_impressions: a.prev_impressions + (s.prev_impressions || 0),
    close: a.close + (s.close_queries || 0),
  }), { clicks: 0, impressions: 0, prev_clicks: 0, prev_impressions: 0, close: 0 }), [sites]);

  return (
    <div className="page">
      <PageHeader
        title="Rankings"
        subtitle="Google search performance for every site (Search Console)"
        actions={(
          <select className="select" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
            {DAY_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        )}
      />
      {overview && overview.error && <Alert type="error">Could not load Search Console data.</Alert>}
      {!overview ? <Spinner label="Loading rankings…" /> : !overview.error && (
        <>
          <div className="kpi-grid">
            <Kpi label="Clicks" value={formatCount(tot.clicks)} sub={<PctDelta cur={tot.clicks} prev={tot.prev_clicks} />} hint={`vs the ${days} days before`} />
            <Kpi label="Impressions" value={formatCount(tot.impressions)} sub={<PctDelta cur={tot.impressions} prev={tot.prev_impressions} />} />
            <Kpi label="Sites with data" value={formatCount(sites.filter((s) => s.impressions).length)} sub={`of ${sites.length} properties`} />
            <Kpi label="Close to page one" value={formatCount(tot.close)} sub="queries at positions 8–20" />
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">Top opportunities across sites</h2></div>
            <p className="muted small" style={{ marginTop: 0 }}>
              Queries at positions 8–20 on the busiest sites, ranked by the value of reaching #3 (estimated from impressions, position and each site&apos;s revenue per visit).
            </p>
            {!opps ? <Spinner label="Scoring opportunities…" /> : opps.error ? <Alert type="error">Could not load opportunities.</Alert>
              : <ValueTable rows={(opps.opportunities || []).slice(0, 25)} showSite />}
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">Sites</h2></div>
            <input className="input" style={{ maxWidth: 260, marginBottom: 12 }} placeholder="Filter sites" value={text}
                   onChange={(e) => setText(e.target.value)} aria-label="Filter sites" />
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortTh k="domain_id" sort={sort} setSort={setSort} num={false}>Site</SortTh>
                    <SortTh k="clicks" sort={sort} setSort={setSort}>Clicks</SortTh>
                    <SortTh k="impressions" sort={sort} setSort={setSort}>Impr</SortTh>
                    <SortTh k="position" sort={sort} setSort={setSort}>Avg pos</SortTh>
                    <th className="num">Change</th>
                    <SortTh k="tracked_queries" sort={sort} setSort={setSort} title="Queries at positions 1–20 with 30+ impressions">Tracked</SortTh>
                    <SortTh k="close_queries" sort={sort} setSort={setSort} title="Tracked queries at positions 8–20">Close</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.domain_id}>
                      <td><Link to={`/domain/${s.domain_id}/rankings`}>{s.domain_id.replace(/_/g, '.')}</Link></td>
                      <TrendCell values={s.trend?.clicks} metric="Clicks" days={days}>
                        {formatCount(s.clicks)} <PctDelta cur={s.clicks} prev={s.prev_clicks} />
                      </TrendCell>
                      <TrendCell values={s.trend?.impressions} metric="Impressions" days={days}>
                        {formatCount(s.impressions)} <PctDelta cur={s.impressions} prev={s.prev_impressions} />
                      </TrendCell>
                      <TrendCell values={s.trend?.position} metric="Average position" days={days} invert>
                        {formatPos(s.position)}
                      </TrendCell>
                      <td className="num"><PosDelta cur={s.position} prev={s.prev_position} /></td>
                      <td className="num">{s.tracked_queries ? <Link to={`/domain/${s.domain_id}/rankings?tab=tracked`}>{formatCount(s.tracked_queries)}</Link> : 0}</td>
                      <td className="num">{s.close_queries ? <Link to={`/domain/${s.domain_id}/rankings?tab=opportunities`}>{s.close_queries}</Link> : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {emptyCount > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowEmpty(!showEmpty)}>
                {showEmpty ? 'Hide sites with no data' : `Show ${emptyCount} sites with no impressions in either period`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RankingsFleet;
